import React from 'react';
import { Handle, Position } from 'reactflow';
import { FlowNodeData } from '../../types';
import { getCountryFlag } from '../../utils/countryFlags';
import NodeContextMenu from '../NodeContextMenu';

interface CompanyNodeProps {
  data: FlowNodeData;
  selected: boolean;
}

const CompanyNode: React.FC<CompanyNodeProps> = ({ data, selected: _selected }) => {
  const flag = getCountryFlag(data.country);
  const hasMultipleShareholders = data.shareholders && data.shareholders.length > 1;
  const shareholderCount = data.shareholders?.length || 0;
  const isRootNode = !data.shareholders || data.shareholders.length === 0 || 
                     data.shareholders.every(s => s.isExternal);
  const isExternalNode = data.country === 'EXT';
  
  // Apply custom styling if available
  const customColor = data.customization?.color;
  const customShape = data.customization?.shape || 'rounded';
  const customOpacity = data.customization?.opacity || 1;
  
  // Check if node has directors
  const hasDirectors = data.directors && data.directors.length > 0;
  const showDirectorsBlock = hasDirectors && data.showDirectors !== false;

  // Shareholder block visibility: show with single shareholder by default,
  // but require >= 2 when showSingleShareholder is explicitly false.
  const minShareholderCount = data.showSingleShareholder !== false ? 1 : 2;
  const shareholderModeOk =
    data.percentageDisplayMode === 'card' || data.percentageDisplayMode === 'both';
  const showShareholderBlock =
    (data.shareholders?.length || 0) >= minShareholderCount && shareholderModeOk;

  // Dynamic height based on shareholders, directors, name length, and shape.
  // Name kan wrappen naar 2 regels bij smalle kaarten (220px) — schat dat
  // ruwweg in op basis van karakter-aantal en pas extra hoogte toe.
  const nameNeedsTwoLines = (data.name?.length || 0) > 22;
  let baseHeight = hasMultipleShareholders ? 120 : 100; // More height when there are shareholders
  if (hasDirectors) baseHeight += 18; // Extra height for directors line
  if (nameNeedsTwoLines) baseHeight += 18; // Extra height voor 2-regel naam
  if (customShape === 'triangle') {
    baseHeight = hasMultipleShareholders ? 140 : 120;
    if (hasDirectors) baseHeight += 18;
    if (nameNeedsTwoLines) baseHeight += 18;
  } else if (customShape === 'oval') {
    baseHeight = hasMultipleShareholders ? 130 : 110;
    if (hasDirectors) baseHeight += 18;
    if (nameNeedsTwoLines) baseHeight += 18;
  }
  const extraHeightPerShareholder = hasMultipleShareholders ? Math.min((shareholderCount - 1) * 12, 40) : 0;
  const nodeHeight = baseHeight + extraHeightPerShareholder;
  
  // Determine styling based on selection state and node type
  let borderStyle = '1px solid #dee2e6';
  let boxShadow = 'none';
  let backgroundStyle = customColor ? 
    `linear-gradient(135deg, ${customColor}, ${customColor}dd)` : 
    'linear-gradient(135deg, #f8f9fa, #e9ecef)';
  let additionalClasses = '';
  let borderRadius = '8px';
  
  // Shape customization
  let clipPath = 'none';
  let shapePadding = '12px';
  let contentMaxWidth = '100%';
  let contentPaddingTop = '0px';
  // Smal padding zodat lange bedrijfsnamen meer horizontale ruimte hebben.
  // 28px × 2 = 56px padding op een 220px kaart laat ~164px voor tekst.
  let nameHorizontalPadding = '28px';
  let nameFontSize = '14px';
  let nameLineHeight = '1.2';
  let flagTopPosition = '8px';
  let flagRightPosition = '8px';
  
  switch (customShape) {
    case 'oval':
      borderRadius = '50%';
      shapePadding = '20px 40px'; // More padding for oval to keep content within ellipse
      contentMaxWidth = '85%'; // Restrict width to stay within oval bounds
      nameHorizontalPadding = '35px'; // Less padding for more name space
      flagRightPosition = '15px'; // Adjust flag position
      break;
    case 'triangle':
      borderRadius = '8px';
      clipPath = 'polygon(50% 8%, 92% 92%, 8% 92%)';
      shapePadding = '45px 30px 20px 30px'; // Significant top padding for triangle
      contentMaxWidth = '75%'; // Narrower content for triangle shape
      contentPaddingTop = '5px'; // Extra spacing from top
      nameHorizontalPadding = '25px'; // Less padding for narrower triangle
      flagTopPosition = '22px'; // Move flag down to be within triangle
      flagRightPosition = '50%'; // Center horizontally
      break;
    default: // 'rounded'
      borderRadius = '8px';
      shapePadding = '12px';
  }
  
  // First apply base styling based on node type
  if (isExternalNode) {
    // External shareholders get gray styling (unless customized)
    if (!customColor) {
      backgroundStyle = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)';
    }
    borderStyle = '2px solid #9ca3af';
    boxShadow = '0 2px 8px rgba(156, 163, 175, 0.3)';
    additionalClasses += ' external-node';
  } else if (isRootNode && !customColor) {
    // Root nodes get blue styling (unless customized)
    backgroundStyle = 'linear-gradient(135deg, #dbeafe, #bfdbfe)';
    borderStyle = '2px solid #3b82f6';
    boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
    additionalClasses += ' root-node';
  }
  
  // Then override with selection/highlight styling (takes precedence over root/external styling)
  if (data.isSelected || data.isInOwnershipPath) {
    // Selected node and parent nodes in ownership path: gold border (brand color)
    // This overrides root blue and external gray styling
    borderStyle = '3px solid #ffd700';
    boxShadow = '0 0 10px rgba(255, 215, 0, 0.5)';
  }

  if (data.isVirtualRoot) {
    return (
      <>
        <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
        <div 
          className="company-node virtual-root"
          style={{
            background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            border: '2px dashed #2196f3',
            color: '#1565c0',
            width: '240px',
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '8px',
            padding: '12px',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center',
            lineHeight: '1.3'
          }}
        >
          <div style={{ fontSize: '20px', marginBottom: '8px' }}>🏢</div>
          <div>{data.name}</div>
          <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.8 }}>
            Container voor aparte hiërarchieën
          </div>
        </div>
        <Handle type="source" position={Position.Bottom} />
      </>
    );
  }

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onMenuToggle) {
      data.onMenuToggle();
    }
  };

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div 
        className={`company-node-wrapper`}
        style={{
          width: '220px',
          height: `${nodeHeight}px`,
          position: 'relative'
        }}
      >
        <div 
          className={`company-node ${additionalClasses}`}
          onClick={handleNodeClick}
          style={{
            background: backgroundStyle,
            border: borderStyle,
            boxShadow: boxShadow,
            color: customColor ? 'white' : '#333',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: borderRadius,
            padding: shapePadding,
            position: 'relative',
            opacity: customOpacity,
            clipPath: clipPath
          }}
          title={hasMultipleShareholders ? `Shareholders:\n${data.ownershipInfo}` : ''}
        >
        {/* Control buttons */}
        <div style={{ 
          position: 'absolute', 
          top: flagTopPosition, 
          right: flagRightPosition, 
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          transform: customShape === 'triangle' ? 'translateX(50%)' : 'none'
        }}>
          {/* Country flag or code */}
          {data.showCountryFlags ? (
            <span style={{ fontSize: '16px' }}>{flag}</span>
          ) : (
            <span style={{ 
              fontSize: '11px', 
              fontWeight: '600', 
              color: '#555',
              backgroundColor: 'rgba(255,255,255,0.9)',
              padding: '2px 4px',
              borderRadius: '3px',
              border: '1px solid rgba(0,0,0,0.15)'
            }}>
              {data.country}
            </span>
          )}
        </div>
        
        {/* Company info */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px',
          maxWidth: contentMaxWidth,
          margin: '0 auto',
          width: '100%',
          paddingTop: contentPaddingTop
        }}>
          <div 
            className="company-name" 
            style={{ 
              color: '#333', 
              paddingLeft: nameHorizontalPadding,
              paddingRight: nameHorizontalPadding, 
              fontWeight: '600', 
              lineHeight: nameLineHeight,
              fontSize: nameFontSize,
              marginBottom: '4px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              textAlign: 'center'
            }}
          >
            {data.name}
          </div>
          {showDirectorsBlock && (
            <div
              style={{
                fontStyle: 'italic',
                fontSize: '10px',
                lineHeight: '1.3',
                color: customColor ? 'rgba(255,255,255,0.85)' : '#666',
                textAlign: 'center',
                paddingLeft: nameHorizontalPadding,
                paddingRight: nameHorizontalPadding,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={data.directors!.join(', ')}
            >
              {data.directors!.join(', ')}
            </div>
          )}
          {isExternalNode && (
            <div 
              className="company-type" 
              style={{ 
                color: '#777', 
                fontSize: '11px',
                fontWeight: '500',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              External Shareholder
            </div>
          )}
        
          {/* Ownership info - only show when mode is 'card' or 'both' */}
          {showShareholderBlock && (
            <div 
              className="ownership-info" 
              style={{ 
                color: '#555', 
                fontSize: '8.5px',
                lineHeight: '1.3',
                paddingTop: '6px',
                marginTop: '2px',
                borderTop: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '2px', color: '#666' }}>
                Shareholders:
              </div>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '1px'
              }}>
                {data.shareholders?.map((sh, idx) => (
                  <div key={idx} style={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    • {sh.name} ({sh.percentage}%)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
        
        {/* Context menu - only for non-external nodes - positioned outside clipped area */}
        {!isExternalNode && (
          <NodeContextMenu
            nodeId={data.id}
            nodeName={data.name}
            isExpanded={data.isExpanded || false}
            hasChildren={data.hasChildren || false}
            customization={data.customization}
            isOpen={data.isMenuOpen || false}
            onToggleExpand={data.onToggleExpand || (() => {})}
            onToggleHide={data.onToggleHide || (() => {})}
            onCustomizeNode={data.onCustomizeNode || (() => {})}
            onIsolateTree={data.onIsolateTree || (() => {})}
            onClose={() => data.onMenuToggle && data.onMenuToggle()}
          />
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
};

export default CompanyNode;
